import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useShop } from "@/contexts/ShopContext";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { AUDIT } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { AlertTriangle, AlertCircle, Info, ChevronRight } from "lucide-react";
import AuditIssueDialog from "@/components/AuditIssueDialog";
import RetestAllButton from "@/components/RetestAllButton";

function scoreTone(v) {
  if (v >= 80) return "text-[hsl(var(--chart-3))]";
  if (v >= 60) return "text-[hsl(var(--chart-2))]";
  return "text-destructive";
}

const CWV_THRESHOLDS = {
  lcp: { good: 2.5, poor: 4.0, unit: "s" },
  inp: { good: 200, poor: 500, unit: "ms" },
  cls: { good: 0.1, poor: 0.25, unit: "" },
  fcp: { good: 1.8, poor: 3.0, unit: "s" },
};

function cwvBucket(name, value) {
  const th = CWV_THRESHOLDS[name];
  if (!th) return "notice";
  if (value <= th.good) return "good";
  if (value <= th.poor) return "warning";
  return "poor";
}

const bucketTone = {
  good: "text-[hsl(var(--chart-3))]",
  warning: "text-[hsl(var(--chart-2))]",
  poor: "text-destructive",
};

const severityMeta = {
  critical: { tone: "text-destructive", bg: "bg-destructive/10", icon: AlertCircle },
  warning: { tone: "text-[hsl(var(--chart-2))]", bg: "bg-[hsl(var(--chart-2))]/10", icon: AlertTriangle },
  notice: { tone: "text-muted-foreground", bg: "bg-secondary", icon: Info },
};

export default function SiteAudit() {
  const { selectedShopId, selectedShop } = useShop();
  const [openIssueId, setOpenIssueId] = useState(null);
  const { t } = useLang();

  const { data: audit } = useQuery({
    queryKey: ["audit", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/audit`)).data,
    enabled: !!selectedShopId,
  });

  return (
    <div data-testid={AUDIT.root} className="space-y-6 rk-fade-in">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
            {selectedShop?.domain}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            {t("nav_audit")}
          </h1>
        </div>
        {selectedShopId && <RetestAllButton shopId={selectedShopId} />}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Health Score */}
        <div
          data-testid={AUDIT.healthScore}
          className="lg:col-span-1 border border-border bg-card rounded-lg p-6 flex flex-col justify-between"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
            {t("audit_health")}
          </div>
          <div className="mt-4 flex items-end gap-3">
            <div
              className={cn(
                "font-mono text-6xl tabular tracking-tighter",
                scoreTone(audit?.health_score ?? 0),
              )}
            >
              {audit ? audit.health_score : "—"}
            </div>
            <div className="pb-3 text-sm text-muted-foreground font-mono">/100</div>
          </div>

          <div className="mt-6 grid grid-cols-4 gap-3">
            {[
              { label: t("audit_crawlability"), value: audit?.crawlability },
              { label: t("audit_performance"), value: audit?.performance },
              { label: t("audit_onpage"), value: audit?.on_page },
              { label: t("audit_links"), value: audit?.links_score },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-[9px] uppercase tracking-[0.14em] font-mono text-muted-foreground">
                  {s.label}
                </div>
                <div className={cn("font-mono text-lg tabular mt-1", scoreTone(s.value ?? 0))}>
                  {s.value ?? "—"}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-border grid grid-cols-3 gap-3 text-center">
            <SummaryStat label={t("errors")} value={audit?.errors_count} tone="destructive" />
            <SummaryStat label={t("warnings")} value={audit?.warnings_count} tone="warning" />
            <SummaryStat label={t("notices")} value={audit?.notices_count} tone="muted" />
          </div>
        </div>

        {/* Core Web Vitals */}
        <div
          data-testid={AUDIT.cwvCard}
          className="lg:col-span-2 border border-border bg-card rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-medium tracking-tight">
              {t("core_web_vitals")}
            </h2>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              {t("crawled_pages")}: {audit ? audit.crawled_pages : "—"}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {["lcp", "inp", "cls", "fcp"].map((k) => {
              const val = audit?.core_web_vitals?.[k];
              const bucket = val !== undefined ? cwvBucket(k, val) : "warning";
              const th = CWV_THRESHOLDS[k];
              return (
                <div key={k} className="border border-border rounded-md p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
                    {k}
                  </div>
                  <div className={cn("mt-3 font-mono text-3xl tabular tracking-tighter", bucketTone[bucket])}>
                    {val !== undefined ? `${val}${th.unit}` : "—"}
                  </div>
                  <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                    {t(bucket === "good" ? "good" : bucket === "warning" ? "needs_improvement" : "poor")}
                  </div>
                  <div className="mt-2 text-[10px] font-mono text-muted-foreground">
                    ≤ {th.good}
                    {th.unit} · good
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Issues list */}
      <div
        data-testid={AUDIT.issuesList}
        className="border border-border bg-card rounded-lg overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display text-lg font-medium tracking-tight">{t("audit_issues")}</h2>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
            {audit?.issues?.length ?? 0} groups
          </div>
        </div>
        <ul>
          {(audit?.issues ?? []).map((issue, i) => {
            const meta = severityMeta[issue.severity] ?? severityMeta.notice;
            const Icon = meta.icon;
            return (
              <li
                key={i}
                data-testid={AUDIT.issueRow(i)}
              >
                <button
                  type="button"
                  onClick={() => setOpenIssueId(issue.issue_id)}
                  disabled={!issue.issue_id}
                  data-testid={`audit-issue-open-${issue.issue_id || i}`}
                  className="w-full text-left px-5 py-4 border-b border-border/70 last:border-0 flex items-start gap-4 hover:bg-secondary/40 transition-colors"
                >
                  <div className={cn("w-8 h-8 rounded-md grid place-items-center shrink-0", meta.bg)}>
                    <Icon className={cn("h-4 w-4", meta.tone)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className={cn("text-[10px] font-mono uppercase tracking-[0.18em]", meta.tone)}>
                        {t(issue.severity)}
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                        {issue.category}
                      </span>
                    </div>
                    <div className="mt-1 font-medium text-sm">{issue.title}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{issue.description}</div>
                  </div>
                  <div className="text-right shrink-0 flex items-center gap-3">
                    <div>
                      <div className="font-mono text-2xl tabular">{issue.count}</div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                        {t("count")}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <AuditIssueDialog
        shopId={selectedShopId}
        issueId={openIssueId}
        open={!!openIssueId}
        onOpenChange={(v) => !v && setOpenIssueId(null)}
      />
    </div>
  );
}

function SummaryStat({ label, value, tone }) {
  const cls =
    tone === "destructive"
      ? "text-destructive"
      : tone === "warning"
        ? "text-[hsl(var(--chart-2))]"
        : "text-muted-foreground";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] font-mono text-muted-foreground">
        {label}
      </div>
      <div className={cn("font-mono text-lg tabular mt-1", cls)}>{value ?? "—"}</div>
    </div>
  );
}
